import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SetupUserPasswordDto } from '@buspro/web/apis';
import { TenantUsersApi, TenantUsersDto } from '@buspro/web/apis';
import {
  ApiError,
  ErrorDisplayComponent,
  PasswordValidatorService,
  ProblemDetail,
  WEB_CONFIG,
  WebAppEnvironment,
  convertToProblemDetail,
  syncAutofilledInputs,
  useErrorHandler,
} from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NgxLoadingModule } from 'ngx-loading';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-activate-tenant',
  templateUrl: './activate-tenant.component.html',
  styleUrls: ['./activate-tenant.component.scss'],
  imports: [
    NgIf,
    AsyncPipe,
    NzAlertComponent,
    ReactiveFormsModule,
    RouterLink,
    NzResultComponent,
    NgxLoadingModule,
    NzSkeletonComponent,
    NgClass,
    ErrorDisplayComponent,
    NzSpinComponent,
  ],
  standalone: true,
})
export class ActivateTenantComponent implements OnInit {
  private fb = inject(FormBuilder);
  private logger = inject(NGXLogger);
  private tenantUsersApi = inject(TenantUsersApi);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private router = inject(Router);

  // Password requirements from feature flags
  passwordRequirements = this.getPasswordRequirements();
  passwordHint = PasswordValidatorService.getHintMessage(this.passwordRequirements);

  private getPasswordRequirements() {
    const strictValidationEnabled = this.environment.features?.strictPasswordValidation ?? true;

    if (!strictValidationEnabled) {
      return {
        minLength: 3,
        requireUppercase: false,
        requireLowercase: false,
        requireNumber: false,
        requireSpecialChar: false,
        enforced: false,
      };
    }

    return {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSpecialChar: true,
      enforced: true,
    };
  }

  form = this.fb.group({
    email: [{ value: '', disabled: true }, [Validators.required]],
    password: ['', [Validators.required, PasswordValidatorService.createValidator(this.passwordRequirements)]],
    passwordConfirm: ['', [Validators.required]],
  });

  private accountFormRef = viewChild<ElementRef<HTMLFormElement>>('accountForm');

  user$ = new BehaviorSubject<TenantUsersDto | null>(null);

  activationToken = signal<string | null>(null);
  isSubmitting = signal<boolean>(false);
  isPasswordVisible = signal<boolean>(false);
  isPasswordConfirmVisible = signal<boolean>(false);
  passwordStrength = signal<number>(0);

  // Email verification step tracking
  emailVerificationStep = signal<'verifying' | 'verified' | 'error'>('verifying');

  pageErrorHandler = useErrorHandler();
  // Error state management
  showActivationError = signal(false);
  activationErrorDetails: ProblemDetail | null = null;

  query = injectQuery(() => ({
    enabled: this.activationToken() !== null,
    queryKey: ['activation-details', this.activationToken()],
    queryFn: () => {
      const token = this.activationToken();
      if (!token) {
        this.showActivationError.set(true);
        this.activationErrorDetails = {
          ...convertToProblemDetail({}),
          detail: 'Invalid activation token',
          title: 'Ops! Something went wrong here',
        } as ProblemDetail;

        return Promise.reject(new Error('Invalid activation token'));
      }

      return lastValueFrom(
        this.tenantUsersApi.tenantUsersControllerGetUserDetailsFromActivationToken({
          token,
        }),
      ).catch((error) => {
        const result = this.pageErrorHandler.handleError(error);

        this.showActivationError.set(true);
        this.activationErrorDetails = result.problemDetail;

        throw new Error(error);
      });
    },
    select: (data: TenantUsersDto) => {
      console.log('tenantUsersControllerGetUserDetailsFromActivationToken', data);
      this.form.get('email')?.setValue(data.email);
      this.user$.next(data);
    },
    throwOnError: true,
    retry: false,
  }));

  get queryError() {
    return this.query.error;
  }

  constructor(
    private route: ActivatedRoute,
    public notification: NzNotificationService,
  ) {}

  ngOnInit(): void {
    const { actkn, uid, cid } = this.route.snapshot.queryParams;
    console.log('uid', uid);
    console.log('cid', cid);
    console.log('actkn', actkn);

    this.logger.log('Getting activation details with token', actkn);

    this.activationToken.set(actkn);

    // Step 1: Verify email automatically (controlled by feature flag)
    const emailVerificationRequired = this.environment.features?.emailVerificationRequired ?? true;

    if (actkn && emailVerificationRequired) {
      this.verifyEmail(actkn);
    } else if (actkn) {
      // Email verification disabled via feature flag - skip verification
      this.logger.log('Email verification disabled via feature flag - skipping verification');
      this.emailVerificationStep.set('verified');
    }

    const passwordControl = this.form.get('password');
    passwordControl?.valueChanges.subscribe((value: string | null) => {
      this.passwordStrength.set(this.calculatePasswordStrength(value ?? ''));
    });
  }

  verifyEmail(token: string) {
    this.emailVerificationStep.set('verifying');

    this.tenantUsersApi.tenantUsersControllerVerifyEmail({ body: { token } }).subscribe({
      next: (result: any) => {
        this.logger.log('Email verified successfully', result);
        this.emailVerificationStep.set('verified');
        this.user$.next({ id: result.userId, email: result.email } as TenantUsersDto);
        this.form.get('email')?.setValue(result.email);
      },
      error: (error: any) => {
        this.logger.error('Email verification failed', error);
        this.emailVerificationStep.set('error');
        const result = this.pageErrorHandler.handleError(error);
        this.showActivationError.set(true);
        this.activationErrorDetails = result.problemDetail;
      },
    });
  }

  completeAccountSetup() {
    // Adopt anything the browser filled in. Values assigned straight onto an
    // input never reach the control, so without this the guard below can reject
    // a form whose fields are visibly complete.
    syncAutofilledInputs(this.form, this.accountFormRef()?.nativeElement);

    if (this.form.get('password')?.value !== this.form.get('passwordConfirm')?.value) {
      this.notification.error('Passwords does not match', 'Please check your password');
      return;
    }

    if (this.form.invalid) {
      // The button is no longer disabled while invalid, so this guard is what
      // stops an incomplete submit — and marking the group touched is what
      // makes the per-field messages appear instead of nothing happening.
      this.form.markAllAsTouched();
      this.notification.warning('Invalid form', 'Please resolve validation errors.');
      return;
    }

    const currentUser = this.user$.getValue();
    const selectedPassword = this.form.get('password')?.value ?? '';
    if (!currentUser?.id) {
      this.notification.error('Invalid user', 'Missing user identifier.');
      return;
    }
    const payload: SetupUserPasswordDto = {
      userId: currentUser.id,
      password: selectedPassword,
    };

    this.isSubmitting.set(true);
    this.form.disable({ emitEvent: false });
    this.tenantUsersApi.tenantUsersControllerCompleteUserAccountSetup({ body: payload }).subscribe({
      next: () => {
        const userName = this.user$.getValue()?.name ?? '';
        this.router.navigate(['/passport/setup-success'], {
          queryParams: { name: userName },
          replaceUrl: true,
        });
      },
      error: (error: ApiError) => {
        this.logger.error(error);
        this.notification.error(error.error.title, error.error.detail);
        this.isSubmitting.set(false);
        this.form.enable({ emitEvent: false });
        this.form.get('email')?.disable({ emitEvent: false });
      },
    });
  }

  private calculatePasswordStrength(password: string): number {
    let score = 0;
    if (!password) return score;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    // Normalize to 0..4
    return Math.min(score, 4);
  }
}
